/**
 * PresenceToggle — beschikbaar / afwezig voor de ring group.
 *
 * Leest en schrijft via de gedeelde presence-store (lib/telephony/
 * presence-store.ts): optimistic update, live sync over alle tabs via de
 * phone-bus en een refetch bij tab-focus. Alleen gebruikers met presence
 * 'available' (én ring_enabled) rinkelen mee bij een inkomend gesprek;
 * afwezig is een bewuste keuze die persist over sessies.
 *
 * `compact` = klein knopje voor de softphone-pill; anders een nette rij met
 * uitleg voor /telefoon → Instellingen.
 */

import { useCallback, useSyncExternalStore } from 'react';
import clsx from 'clsx';
import {
  getPresenceState,
  setPresence,
  subscribePresence,
} from '../../lib/telephony/presence-store';

export function PresenceToggle({ compact = false }: { compact?: boolean }) {
  const state = useSyncExternalStore(subscribePresence, getPresenceState);

  const toggle = useCallback(() => {
    void setPresence(state.presence === 'available' ? 'dnd' : 'available');
  }, [state.presence]);

  const isAway = state.presence === 'dnd';

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={state.saving || !state.loaded}
        aria-pressed={isAway}
        title={
          isAway
            ? 'Afwezig — je rinkelt niet mee. Klik om beschikbaar te worden.'
            : 'Beschikbaar — klik om jezelf op afwezig te zetten (pauze, even weg).'
        }
        className={clsx(
          'flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors',
          isAway ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'text-gray-500 hover:bg-gray-100',
        )}
      >
        <span
          className={clsx('w-2 h-2 rounded-full', isAway ? 'bg-amber-500' : 'bg-emerald-500')}
          aria-hidden
        />
        {isAway ? 'Afwezig' : 'Beschikbaar'}
      </button>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-slate-800">
          {isAway ? 'Afwezig' : 'Beschikbaar voor gesprekken'}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          {isAway
            ? 'Je rinkelt niet mee bij inkomende gesprekken (pauze, even van je plek). Uitbellen kan gewoon.'
            : 'Inkomende gesprekken rinkelen bij jou (en bij collega’s die beschikbaar zijn).'}
          {!state.provisioned && state.loaded && (
            <span className="block text-amber-600 mt-1">
              Je hebt nog geen eigen belregistratie — vraag een admin om je toe te voegen via Telefoon-instellingen.
            </span>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={state.saving || !state.loaded}
        aria-pressed={isAway}
        className={clsx(
          'shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
          isAway
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-amber-500 text-white hover:bg-amber-600',
        )}
      >
        {isAway ? 'Beschikbaar worden' : 'Zet op afwezig'}
      </button>
    </div>
  );
}
